import { type Draft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  type FormContainerVariantType,
  WrapperField,
} from '@blaze-runtime/engine-domain';

import { arePropsWithTitle } from '../../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import {
  getWizardQuestionFieldOfForm,
  getWizardSteps,
} from '../../../elements/wizard/wizard.utils';

import { type QuestionForm } from './wizard-question-forms';

/**
 * Makes the field inside each question's form what a questionnaire needs of it: reachable
 * from its step, and carrying no title of its own.
 *
 * The step remembers which element is its field the way it remembers its texts - the panel's
 * Placeholder knob edits that element directly and has to resolve it from props alone.
 *
 * A question's form shows the question as a Text element above the field, so the field's own
 * title would only repeat it. Titles are hidden where they were never touched
 * (`enabled === undefined`), so a label the builder re-enabled on purpose survives every later
 * regeneration.
 *
 * Runs after each question form's own generation, which is what creates the fields.
 */
export function applyQuestionFieldsMutably(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
  forms: QuestionForm[],
): void {
  const wizard = draft.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    throw new Error('element is not a wizard');
  }
  const steps = getWizardSteps(wizard.props);

  for (const { stepIndex, columnKey, formId } of forms) {
    const step = steps[stepIndex];
    const form = draft.entities[formId];
    if (!step || !isVariantOfFormContainer(form)) {
      continue;
    }

    const field = getWizardQuestionFieldOfForm(draft.entities, form, columnKey);
    if (field) {
      step.fieldElementId = field.id;
    }
    hideGeneratedFieldTitlesMutably(draft, form);
  }
}

/** Hides the never-touched titles of the form's generated fields, label and reserved width. */
function hideGeneratedFieldTitlesMutably(
  draft: Draft<EngineStateBase>,
  form: CanvasElementFrom<FormContainerVariantType>,
): void {
  for (const [refId, fieldElementId] of Object.entries(getWrapperRefs(form))) {
    if (!WrapperField.isRefId(refId)) {
      continue;
    }
    const field = draft.entities[fieldElementId];
    if (!arePropsWithTitle(field) || field.props.content.title.enabled !== undefined) {
      continue;
    }

    field.props.content.title.enabled = false;
  }
}
